import { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import type { CommunicationPattern } from '../../hooks/usePostProcessingMetrics';

interface Props {
  pattern: CommunicationPattern;
  onGroupClick?: (group: string) => void;
}

export function ChordDiagram({ pattern, onGroupClick }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onGroupClickRef = useRef(onGroupClick);
  onGroupClickRef.current = onGroupClick;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !pattern.groups.length) return;

    function draw() {
      if (!container) return;
      while (container.firstChild) container.removeChild(container.firstChild);

      const outerRadius = Math.min((window.innerWidth * 0.9) / 2, 450);
      const innerRadius = outerRadius - 130;
      const fill = d3.scaleOrdinal((d3 as any).schemeAccent);

      const svg = d3.select(container).append('svg')
        .style('width', String(outerRadius * 1.98))
        .style('height', String(outerRadius * 1.98))
        .style('display', 'initial')
        .attr('id', 'circle')
        .append('g')
        .attr('transform', `translate(${outerRadius},${outerRadius})`);

      const ribbon = d3.ribbon().radius(innerRadius);

      const chord = d3.chord()
        .padAngle(0.04)
        .sortGroups(d3.descending)
        .sortSubgroups(d3.descending)
        .sortChords(d3.descending)(pattern.matrix);

      const chords = svg.selectAll<SVGPathElement, d3.ChordSubgroup>('.chord')
        .data(chord)
        .enter().append('path')
        .attr('class', 'chord')
        .style('fill', (_, i) => fill(`${i}`))
        .attr('d', ribbon as any);

      const arc = d3.arc()
        .innerRadius(innerRadius)
        .outerRadius(innerRadius + 20);

      const g = svg.selectAll('.group')
        .data(chord.groups)
        .enter().append('g')
        .attr('class', 'group')
        .on('mouseover', (_d, i) => {
          chords.classed('fade', (p: any) => p.source.index !== i && p.target.index !== i);
        })
        .on('mouseout', () => chords.classed('fade', () => false))
        .on('click', (_d, i) => {
          onGroupClickRef.current?.(pattern.groups[i]);
        });

      g.append('path')
        .style('fill', (_d, i) => fill(`${i}`))
        .style('stroke', (_d, i) => fill(`${i}`))
        .attr('d', arc as any);

      svg.append('g').selectAll('.arc')
        .data(chord.groups)
        .enter().append('svg:text')
        .attr('dy', '.35em')
        .attr('text-anchor', (d: any) => ((d.startAngle + d.endAngle) / 2) > Math.PI ? 'end' : null)
        .attr('transform', (d: any) => {
          const mid = (d.startAngle + d.endAngle) / 2;
          return `rotate(${mid * 180 / Math.PI - 90})translate(${innerRadius + 25})${mid > Math.PI ? 'rotate(180)' : ''}`;
        })
        .text((d: any) => {
          const label = pattern.groups[d.index] ?? '';
          return label.length > 19 ? `${label.substring(0, 16)}...` : label;
        });
    }

    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, [pattern]);

  return <div className="chord" style={{ textAlign: 'center' }} ref={containerRef} />;
}
